<script>
import { mapState, mapActions } from 'vuex';
import {
  isEmpty,
  orderBy,
  sortBy,
  findIndex,
} from 'lodash';
import { eventBus } from 'src/features';
import { l, toast } from 'src/utils';
import api from 'src/api';

export default {
  props: ['value'],
  data() {
    return {
      header: this.value.header,
      channelId: this.value.channelId,
      channelIdentifier: this.value.channelIdentifier,
      currentOperatorId: this.value.currentOperatorId,
      comment: null,
      initialSkillsDirectories: [],
      loadingSkillsDirectories: false,
      loadingSkillDirectory: false,
      lastLoadingDirectoryId: null,
      tableHeaders: [`${this.$t('T_DIRECTORIES')}:`, this.$t('T_OPERATORS_NUMBER_TOTAL'), this.$t('T_ACTIVITIES_NUMBER_IN_QUEUES_TOTAL'), `${this.$t('T_DIRECTORY_SKILLS')}:`, this.$t('T_OPERATORS_NUMBER'), this.$t('T_ACTIVITIES_NUMBER_IN_QUEUE')],
      activeSkillsDirectory: null,
      showActiveDirectoryTooltip: false,
      showSkillTooltip: false,
      availableOperatorsList: [],
      transferWithoutOperators: false,
      currentOperators: [],
      isDirectoryRemoved: false,
      isDirectoryRequestErr: false,
      loadingButtons: false,
    };
  },
  async created() {
    try {
      this.loadingSkillsDirectories = true;
      this.loadingSkillDirectory = true;
      if (this.isOverseer) {
        this.tableHeaders.push(`${this.$t('T_FULL_NAME_OF_AVAILABLE_OPERATORS')}:`);
      }
      this.initialSkillsDirectories = await this.getSkillsDirectoriesForTransfer(this.channelId);
      this.loadingSkillsDirectories = false;
      if (!this.isOverseer) {
        this.loadingSkillDirectory = false;
      }
      if (!isEmpty(this.tableSkillsDirectories)) {
        if (this.isOverseer) {
          this.transferWithoutOperators = await api.getChannelConfigByActivity(this.value.activityId)
            .then((response) => {
              const transferWithoutOperatorsConfig = response
                .find((configItem) => configItem.name === 'transfer_to_queue_without_operators');
              return transferWithoutOperatorsConfig.value;
            });
          this.setActiveSkillDirectoryOverseer(this.tableSkillsDirectories[0].id);
        } else {
          this.setActiveSkillDirectoryOperator(this.tableSkillsDirectories[0]);
        }
      }
    } catch (err) {
      l.error(err);
      toast.error({ msg: this.$t('T_REQUEST_ERROR') });
      this.loadingSkillsDirectories = false;
      this.loadingSkillDirectory = false;
    }
  },
  beforeDestroy() {
    if (this.isOverseer) {
      this.value.onCloseOverlay();
    }
  },
  computed: {
    ...mapState('home', ['user']),
    tableSkillsDirectories() {
      if (isEmpty(this.initialSkillsDirectories)) return [];
      // убираем неактивные навыки из справочников
      this.initialSkillsDirectories
        .forEach((item) => {
          item.requestTypes = item.requestTypes.filter((skill) => skill.requestType.active);
          return item;
        });
      // убираем справочники без навыков
      const nonEmptySkillsDirectories = this.initialSkillsDirectories
        .filter((item) => !isEmpty(item.requestTypes));
      // сортируем справочники по приоритету и по алфавиту
      const orderedSkillsDirectories = orderBy(nonEmptySkillsDirectories, ['priority', (elem) => elem.name.toLowerCase()], ['desc', 'asc']);
      // перемещаем общий справочник на последнюю позицию (идентифицируем по зарезервированному имени "общий")
      const reservedName = 'общий';
      const generalDirectoryIndex = orderedSkillsDirectories.findIndex((item) => item.name.toLowerCase() === reservedName);
      if (generalDirectoryIndex !== -1) {
        const [generalDirectory] = orderedSkillsDirectories
          .splice(generalDirectoryIndex, 1);
        orderedSkillsDirectories.push(generalDirectory);
      }
      return orderedSkillsDirectories;
    },
    isOverseer() {
      return this.user.role === 'overseer';
    },
    msgOnNoFreeOperators() {
      if (!this.transferWithoutOperators) {
        return this.$t('T_NO_FREE_OPERATORS');
      } return this.$t('T_TRANSFER_TO_QUEUE');
    },
    directorySkillsInfoText() {
      // порядок имеет значение
      if (this.isDirectoryRemoved) return this.$t('T_DIRECTORY_IS_REMOVED');
      if (this.isDirectoryRequestErr) return this.$t('T_NO_DATA');
      return this.$t('T_NO_SKILLS');
    },
    showDirectorySkillsInfoText() {
      return (this.activeSkillsDirectory && this.activeSkillsDirectory.requestTypes.length === 0)
        || this.isDirectoryRemoved
        || this.isDirectoryRequestErr;
    },
    showNoData() {
      return isEmpty(this.tableSkillsDirectories) && !this.loadingSkillsDirectories;
    },
    showActiveDirectoryContent() {
      return this.activeSkillsDirectory
        && ((this.isOverseer && !this.loadingSkillDirectory) || !this.isOverseer);
    },
  },
  methods: {
    ...mapActions('activities', ['transferActivity']),
    ...mapActions('skillsDirectories', ['getSkillsDirectoriesForTransfer', 'getSkillDirectoryForTransfer']),
    ...mapActions('operator', ['hideActivity']),
    close() {
      this.$parent.close([null]);
    },
    isDirectoryOrSkillHeader(header) {
      return header === `${this.$t('T_DIRECTORIES')}:` || header === `${this.$t('T_DIRECTORY_SKILLS')}:`;
    },
    isDirectoryHeader(header) {
      return header === `${this.$t('T_DIRECTORIES')}:`;
    },
    isSkillHeader(header) {
      return header === `${this.$t('T_DIRECTORY_SKILLS')}:`;
    },
    isOperatorsHeader(header) {
      return header === `${this.$t('T_FULL_NAME_OF_AVAILABLE_OPERATORS')}:`;
    },
    handleDirectoryActivities(directoryId) {
      const { requestTypes } = this.tableSkillsDirectories.find((item) => item.id === directoryId);
      return requestTypes.reduce((acc, item) => acc + item.activitiesCounter, 0);
    },
    onClickSkillDirectory(skillsDirectory) {
      if (this.isOverseer) {
        this.setActiveSkillDirectoryOverseer(skillsDirectory.id);
      } else {
        this.setActiveSkillDirectoryOperator(skillsDirectory);
      }
    },
    setActiveSkillDirectoryOverseer(directoryId) {
      this.loadingSkillDirectory = true;
      this.lastLoadingDirectoryId = directoryId;
      this.clearDirectoryRequestErrors();
      this.getSkillDirectoryForTransfer({
        directoryId,
        channelIdentifier: this.channelIdentifier,
        channelId: this.channelId,
      }).then((directory) => {
        const index = findIndex(this.initialSkillsDirectories, { id: directory.id });
        this.initialSkillsDirectories[index].commonOperatorsCounter = directory.commonOperatorsCounter;
        this.initialSkillsDirectories[index].requestTypes = directory.requestTypes;
        // убираем неактивные навыки и наполняем данные по активному справочнику
        const filteredRequestTypes = directory.requestTypes.filter((skill) => skill.requestType.active);
        this.activeSkillsDirectory = {
          ...directory,
          requestTypes: orderBy(filteredRequestTypes, ['priority', (elem) => elem.requestType.name.toLowerCase()], ['desc', 'asc']),
        };
        this.currentOperators = directory.requestTypes.map((item) => ({
          skillId: item.requestType.id,
          id: undefined,
          fullName: undefined,
        }));
        if (this.lastLoadingDirectoryId === directoryId) { // показываем лоадер, пока не завершится
          // последний запрос (если юзер несколько кнопок подряд нажал)
          this.loadingSkillDirectory = false;
        }
      })
        .catch((err) => {
          this.activeSkillsDirectory = {
            id: directoryId,
            commonOperatorsCounter: 0,
            requestTypes: [],
          };
          this.currentOperators = [];
          if (err.message === 'GraphQL error: not_found') {
            toast.error({ msg: this.$t('T_DIRECTORY_IS_REMOVED') });
            this.isDirectoryRemoved = true;
          } else {
            toast.error({ msg: this.$t('T_REQUEST_ERROR') });
            this.isDirectoryRequestErr = true;
          }
          if (this.lastLoadingDirectoryId === directoryId) { // показываем лоадер, пока не завершится
          // последний запрос (если юзер несколько кнопок подряд нажал)
            this.loadingSkillDirectory = false;
          }
        });
    },
    setActiveSkillDirectoryOperator(skillsDirectory) {
      this.activeSkillsDirectory = {
        ...skillsDirectory,
        requestTypes: orderBy(skillsDirectory.requestTypes, ['priority', (elem) => elem.requestType.name.toLowerCase()], ['desc', 'asc']),
      };
    },
    clearDirectoryRequestErrors() {
      this.isDirectoryRemoved = false;
      this.isDirectoryRequestErr = false;
    },
    isActiveDirectory(id) {
      return id === this.activeSkillsDirectory?.id;
    },
    mouseOverDirectoryName() {
      const ref = this.$refs.activeDirectory;
      this.showActiveDirectoryTooltip = ref?.scrollWidth > ref?.clientWidth;
    },
    mouseOverSkill(index) {
      const skillRefs = this.$refs[`skill${index}`];
      if (!isEmpty(skillRefs)) {
        const skillRef = skillRefs[0];
        this.showSkillTooltip = skillRef?.scrollWidth > skillRef?.clientWidth;
      }
    },
    onClickTransferBtn(skill) {
      this.loadingButtons = true;
      if (this.isOverseer) {
        this.transferByOverseer(skill);
      } else {
        this.transferByOperator(skill);
      }
    },
    transferByOperator(skill) {
      this.transferActivity({
        id: this.value.activityId,
        requestTypeId: skill.requestType.id,
        comment: this.comment,
      })
        .then(() => {
          eventBus.$emit('activityTransfered', {
            requestTypeName: skill.requestType.name,
          });
          this.hideActivity(this.value.activityId);
          this.close();
          this.loadingButtons = false;
        })
        .catch((err) => {
          if (err.graphQLErrors?.length) {
            this.loadingButtons = false;
          }
        });
    },
    transferByOverseer(skill) {
      const operator = this.currentOperators.find((item) => item.skillId === skill.requestType.id);
      const operatorId = operator?.id ? operator.id : null;
      this.transferActivity({
        id: this.value.activityId,
        requestTypeId: skill.requestType.id,
        operatorId,
        comment: this.comment,
      })
        .then(() => {
          eventBus.$emit('activityTransfered', {
            requestTypeName: skill.requestType.name,
            operatorFullName: operator?.fullName || null,
          });
          eventBus.$emit('search');
          this.close();
          this.loadingButtons = false;
        })
        .catch((err) => {
          if (err.graphQLErrors?.length) {
            this.loadingButtons = false;
          }
        });
    },
    isEmptyOperatorsList(activeDirectoryOperators) {
      if (!activeDirectoryOperators) return true;
      const filteredOperators = activeDirectoryOperators
        .filter((item) => item.id !== this.currentOperatorId);
      return isEmpty(filteredOperators);
    },
    handleOperators(item) {
      const { availableOperators } = item;
      const skillId = item.requestType.id;
      const filteredOperators = sortBy(availableOperators.filter((elem) => elem.id !== this.currentOperatorId),
        (operator) => operator.fullName.toLowerCase());
      return filteredOperators.map((elem) => ({
        skillId,
        ...elem,
      }));
    },
    truncate(text, length = 40) {
      return text.length > length ? `${text.substring(0, length)}...` : text;
    },
    transferDisabled(activeDirectoryOperators) {
      if (this.isOverseer && !this.transferWithoutOperators) {
        return this.isEmptyOperatorsList(activeDirectoryOperators);
      } return false;
    },
    getCurrentOperator(id) {
      const index = findIndex(this.currentOperators, { id });
      return this.currentOperators[index];
    },
    currentOperatorIndex(item) {
      return findIndex(this.currentOperators, { skillId: item.requestType?.id });
    },
    changeCurrentOperator($event, skillData) {
      const index = findIndex(this.currentOperators, { skillId: skillData.requestType.id });
      this.currentOperators[index].id = $event ? $event.id : undefined;
      this.currentOperators[index].fullName = $event ? $event.fullName : undefined;
    },
  },
};
</script>

<template lang="pug">
div
  div(:class="[$style.root, isOverseer && $style.rootOverseer]")
    header(:class="$style.header")
      div(:class="$style.headerTitle",
        data-test="MOVE_TO_OTHER_OPERATOR") {{ header }}
      div(:class="$style.icon", @click="close",
          tabindex="0", @keyup.enter="close")
        ui-icon(:class="$style.close", key="close", :title="$t('T_CLOSE')", data-test="CLOSE_BUTTON") close
    div(:class="$style.line")
    div(:class="[$style.formBody, isOverseer && $style.formBodyOverseer]")
      div(:class="$style.table")
        div(:class="$style.tableHeaders")
          div(v-for="(header, index) in tableHeaders")
            div(:class="[$style.tableHeader, isDirectoryOrSkillHeader(header) && $style.tableHeaderWide, isDirectoryHeader(header) && $style.marginRight, isSkillHeader(header) && $style.tableHeaderSkill, isOperatorsHeader(header) && $style.tableHeaderOperators]") {{ header }}
          v-tooltip(v-if="showActiveDirectoryTooltip", right, content-class="vuetifyTooltip", max-width="300px", z-index="10000")
            template(v-slot:activator="{ on, attrs }")
              div(v-on="on", :class="[$style.activeDirectoryName, isOverseer && $style.activeDirectoryNameOverseer]", @mouseover="mouseOverDirectoryName", ref="activeDirectory") {{ activeSkillsDirectory?.name }}
            div {{ activeSkillsDirectory?.name }}
          div(v-if="!showActiveDirectoryTooltip", :class="[$style.activeDirectoryName, isOverseer && $style.activeDirectoryNameOverseer]", @mouseover="mouseOverDirectoryName", ref="activeDirectory") {{ activeSkillsDirectory?.name }}
        div(:class="$style.tableContent")
          div(:class="$style.directoriesContent")
            div(v-if="tableSkillsDirectories.length && !loadingSkillsDirectories")
              div(v-for="(skillsDirectory, index) in tableSkillsDirectories", :class="[$style.columnsWrap, isOverseer && $style.columnsWrapOverseer, $style.directories]")
                div(:class="[$style.column, $style.columnDirectories, $style.marginRight]")
                  ui-button(data-test="SKILLS_DIRECTORY",
                    :class="[$style.btn, $style.btnRound]",
                    colored="socialVk",
                    size="large",
                    center,
                    :key="skillsDirectory.id",
                    :disabled="isActiveDirectory(skillsDirectory.id)",
                    @click.native="onClickSkillDirectory(skillsDirectory)",
                    @keyup.enter.native="onClickSkillDirectory(skillsDirectory)")
                    span(:class="$style.text") {{ skillsDirectory.name }}
                div(:class="[$style.column, $style.columnCenter, isActiveDirectory(skillsDirectory.id) && $style.columnActive]") {{ skillsDirectory.commonOperatorsCounter }}
                div(:class="[$style.column, $style.columnCenter, isActiveDirectory(skillsDirectory.id) && $style.columnActive]") {{ handleDirectoryActivities(skillsDirectory.id) }}
            v-progress-circular(v-if="loadingSkillsDirectories", color="primary" indeterminate, :class="$style.loadingBar")
          div(:class="[$style.activeDirectoryContent, isOverseer && $style.activeDirectoryContentOverseer]")
            div(v-if="showActiveDirectoryContent")
              div(v-for="(item, index) in activeSkillsDirectory.requestTypes", :class="[$style.columnsWrap, isOverseer && $style.columnsWrapOverseer,  $style.activeDirectory, isOverseer && $style.activeDirectoryOverseer]")
                div(:class="[$style.column, $style.columnWide, $style.columnSkills]")
                  v-tooltip(v-if="showSkillTooltip", right, max-width="300px",content-class="vuetifyTooltip", z-index="10000")
                    template(v-slot:activator="{ on, attrs }")
                      div(v-on="on", @mouseover="mouseOverSkill(index)", :ref="`skill${index}`", :class="$style.ellipsis") {{ item.requestType.name }}
                    div {{ item.requestType.name }}
                  div(v-if="!showSkillTooltip", @mouseover="mouseOverSkill(index)", :ref="`skill${index}`", :class="$style.ellipsis") {{ item.requestType.name }}
                div(:class="[$style.column, $style.columnCenter]") {{ item.operatorsCounter }}
                div(:class="[$style.column, $style.columnCenter]") {{ item.activitiesCounter }}
                div(v-if="isOverseer", :class="$style.select")
                  v-autocomplete(:value="currentOperators[currentOperatorIndex(item)]",
                    @change="changeCurrentOperator($event, item)",
                    return-object,
                    flat,
                    clearable,
                    name="operator",
                    hide-details="true",
                    :label="isEmptyOperatorsList(item.availableOperators) ? msgOnNoFreeOperators : $t('T_OPERATOR')",
                    :items="!isEmptyOperatorsList(item.availableOperators) ? handleOperators(item) : []",
                    item-text="fullName",
                    :menu-props="{ zIndex: 10009, height: '10px' }",
                    :disabled="isEmptyOperatorsList(item.availableOperators)",
                    :no-data-text="$t('NO_DATA_AVAILABLE_TEXT')")
                    template(v-slot:item="{ item }")
                      v-list-item-content
                        v-list-item-title {{ truncate(item.fullName) }}
                div(:class="$style.btnTransfer")
                  ui-button(data-test="TRANSFER_BTN",
                    :class="$style.btn",
                    colored="primary",
                    size="large",
                    center,
                    :key="item.requestType.id",
                    :disabled="transferDisabled(item.availableOperators) || loadingButtons"
                    @click.native="onClickTransferBtn(item)",
                    @keyup.enter.native="onClickTransferBtn(item)")
                    span(:class="$style.text") {{ $t('T_TRANSFER') }}
              div(v-if="showDirectorySkillsInfoText", :class="$style.noSkills") {{ directorySkillsInfoText }}
            v-progress-circular(v-if="loadingSkillDirectory", :class="$style.loadingBar", color="primary" indeterminate)
        div(v-if="showNoData", :class="$style.noData") {{ $t('T_NO_DATA') }}
      v-text-field(v-if="tableSkillsDirectories.length" v-model="comment", :label="$t('T_COMMENT')")
</template>

<style lang="sass" module>
.root
  display: block
  width: 1200px
  height: 620px
  &Overseer
    width: 1480px
.header
  padding: 24px 0 10px
  display: flex
  flex-grow: 1
  justify-content: center
  align-items: center
  &Title
    font-size: 18px
    font-weight: 400
    display: flex
    justify-content: center
    margin-left: 56px
    flex-grow: 1
.icon
  width: 36px
  height: 36px
  display: flex
  align-items: center
  justify-content: center
  border-radius: 50%
  cursor: pointer
  margin-right: 20px
  &:hover
    background: $color_main_light_third
    color: $color_msg_dark
  &:focus
    background: $color_main_light_third
    color: $color_msg_dark
    outline: none
  .close
    width: 70%
.line
  border-bottom: 1px solid $color_divider
  margin: 8px 0
.formBody
  display: flex
  flex-direction: column
  padding: 15px 50px 0
  width: 1200px
  &Overseer
    width: 1480px
.table
  margin-bottom: 25px
  &Headers
    display: flex
    padding: 10px 0
    margin-bottom: 10px
  &Header
    width: 96px
    display: flex
    justify-content: center
    text-align: center
    line-height: 18px
    color: $color_text_second
    white-space: pre-wrap
    font-size: 13px
    &Wide
      width: 200px
      justify-content: flex-start
      font-size: 18px
      font-weight: 400
      color: $color_transparent_black
    &Skill
      margin: 0 20px 0 71px
      justify-content: flex-start
    &Operators
      margin-left: 20px
      width: 250px
      justify-content: flex-start
  &Content
    display: flex
    margin-top: 20px
.directoriesContent
    display: flex
    flex-direction: column
    height: 310px
    overflow: auto
    overflow-x: hidden
    padding-right: 20px
    width: 450px
    scrollbar-width: thin
.activeDirectoryContent
    display: flex
    flex-direction: column
    height: 310px
    overflow: auto
    overflow-x: hidden
    padding-right: 20px
    width: 648px
    margin-left: 20px
    scrollbar-width: thin
    &Overseer
      width: 928px
.directories
  width: 450px
.activeDirectory
  width: 648px
  align-items: center
  &Overseer
    width: 928px
.btn
  width: 100%
  &Round
    border-radius: 16px
  &Transfer
    width: 130px
    margin: 0 20px 0 30px
.column
  width: 96px
  display: flex
  align-items: center
  &Center
    justify-content: center
    align-items: center
  &Active
    background: $color_gray_active
  &Directories
    display: flex
    width: 200px !important
  &Skills
    display: flex
    width: 201px !important
    margin: 0 20px 0 30px
.columnsWrap
  margin: 5px 0
  display: flex
  justify-content: flex-start
  &Overseer
    margin: 9px 0
.marginRight
  margin-right: 30px
.ellipsis
  display: block
  overflow: hidden
  white-space: nowrap
  text-overflow: ellipsis
.activeDirectoryName
  margin: 25px 0 0 -411px
  width: 201px
  overflow: hidden
  white-space: nowrap
  text-overflow: ellipsis
  color: $color_social_vk
  &Overseer
    margin: 25px 0 0 -681px
.select
  width: 250px
  margin: -16px 20px 0
.noSkills
  margin: 16px 0 0 29px
.noData
  color: $color_red
  font-size: 18px
  font-weight: 400
  text-align: center
.loadingBar
  align-self: center
  margin-top: 70px
</style>
